import React, { useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, Check } from 'lucide-react';
interface Trade {
  trade_id: string;
  date: string;
  side: 'Buy' | 'Sell';
  type: 'Market' | 'Limit';
  pair: string;
  price: number;
  volume: number;
  cost: number;
}
const mockTradesData: Trade[] = [{
  trade_id: 'TTUURL',
  date: '1/18/26 4:43 PM',
  side: 'Sell',
  type: 'Market',
  pair: 'BTC/USD',
  price: 95078.1,
  volume: 0.0008267,
  cost: 78.6
}, {
  trade_id: 'T5SYTM',
  date: '1/15/26 11:35 PM',
  side: 'Buy',
  type: 'Limit',
  pair: 'BTC/USD',
  price: 95790.6,
  volume: 0.0008267,
  cost: 79.19
}, {
  trade_id: 'TGEVB7',
  date: '12/12/25 4:18 PM',
  side: 'Sell',
  type: 'Market',
  pair: 'BTC/USD',
  price: 91772.50121,
  volume: 0.00115416,
  cost: 105.92
}, {
  trade_id: 'T6GVOW',
  date: '12/11/25 10:31 PM',
  side: 'Buy',
  type: 'Market',
  pair: 'BTC/USD',
  price: 93487.8006,
  volume: 0.00115416,
  cost: 107.9
}, {
  trade_id: 'T4CKC4',
  date: '12/11/25 10:31 PM',
  side: 'Buy',
  type: 'Market',
  pair: 'BTC/USD',
  price: 93463.7982,
  volume: 0.00186441,
  cost: 174.25
}];
// Helper to get icon URL
const getIconUrl = (pair: string) => {
  const symbol = pair.split('/')[0].toLowerCase();
  const map: Record<string, string> = {
    btc: 'bitcoin-btc',
    eth: 'ethereum-eth',
    sol: 'solana-sol',
    usdt: 'tether-usdt',
    bnb: 'bnb-bnb',
    xrp: 'xrp-xrp',
    ada: 'cardano-ada',
    doge: 'dogecoin-doge'
  };
  const slug = map[symbol] || `${symbol}-${symbol}`;
  return `https://cryptologos.cc/logos/${slug}-logo.svg`;
};
export function TradesTab() {
  const [selectedPairs, setSelectedPairs] = useState<string[]>([]);
  const [showPairFilter, setShowPairFilter] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const clearFilters = () => {
    setSelectedPairs([]);
    setDateFrom('');
    setDateTo('');
  };
  const togglePair = (pair: string) => {
    if (selectedPairs.includes(pair)) {
      setSelectedPairs(selectedPairs.filter(p => p !== pair));
    } else {
      setSelectedPairs([...selectedPairs, pair]);
    }
  };
  const hasActiveFilters = selectedPairs.length > 0 || dateFrom || dateTo;
  // Filter logic (simplified for mock data)
  const filteredData = mockTradesData.filter(trade => {
    if (selectedPairs.length > 0 && !selectedPairs.includes(trade.pair)) return false;
    return true;
  });
  const totalItems = filteredData.length;
  const itemsPerPage = 25;
  const currentPage = 1;
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
  return <div className="space-y-4" data-id="element-504">
      {/* Filters */}
      <div className="glass-border rounded-[20px] p-4" data-id="element-505">
        <div className="flex items-center gap-3 flex-wrap" data-id="element-506">
          {/* Pairs Filter */}
          <div className="relative" data-id="element-507">
            <button onClick={() => setShowPairFilter(!showPairFilter)} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm ${showPairFilter ? 'bg-background-elevated text-text-primary ring-1 ring-brand-purple' : 'bg-background-elevated hover:bg-background-elevated/80 text-text-primary'}`} data-id="element-508">
              Select pairs
              {selectedPairs.length > 0 && <span className="bg-brand-purple text-white text-[10px] px-1.5 py-0.5 rounded-full" data-id="element-509">
                  {selectedPairs.length}
                </span>}
              <ChevronDown size={16} data-id="element-510" />
            </button>
            {showPairFilter && <>
                <div className="fixed inset-0 z-20" onClick={() => setShowPairFilter(false)} data-id="element-511" />
                <div className="absolute top-full left-0 mt-2 w-64 bg-background-elevated border border-border-subtle rounded-xl shadow-2xl z-30 p-3 max-h-80 overflow-y-auto" data-id="element-512">
                  <div className="space-y-1" data-id="element-513">
                    {['BTC/USD', 'ETH/USD', 'SOL/USD', 'BTC/EUR', 'BTC/USDT'].map(pair => <button key={pair} onClick={() => togglePair(pair)} className="w-full flex items-center gap-3 p-2 hover:bg-background-secondary rounded-lg transition-colors text-left group" data-id="element-514">
                        <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${selectedPairs.includes(pair) ? 'bg-brand-purple border-brand-purple' : 'border-text-secondary group-hover:border-text-primary'}`} data-id="element-515">
                          {selectedPairs.includes(pair) && <Check size={10} className="text-white" data-id="element-516" />}
                        </div>
                        <span className="text-sm text-text-primary" data-id="element-517">
                          {pair}
                        </span>
                      </button>)}
                  </div>
                </div>
              </>}
          </div>

          {/* Date Range */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full sm:w-auto" data-id="element-518">
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} placeholder="From" className="px-4 py-2 min-h-[44px] w-full sm:w-auto bg-background-elevated text-text-primary rounded-lg text-sm border border-border-subtle focus:border-border-default outline-none" data-id="element-519" />
            <span className="text-text-secondary hidden sm:inline" data-id="element-520">to</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} placeholder="To" className="px-4 py-2 min-h-[44px] w-full sm:w-auto bg-background-elevated text-text-primary rounded-lg text-sm border border-border-subtle focus:border-border-default outline-none" data-id="element-521" />
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && <button onClick={clearFilters} className="flex items-center gap-2 px-4 py-2 text-text-secondary hover:text-text-primary transition-colors text-sm" data-id="element-522">
              Clear filters
            </button>}
        </div>
      </div>

      {/* Table */}
      <div className="glass-border rounded-[20px] overflow-hidden" data-id="element-523">
        <div className="overflow-x-auto" data-id="element-524">
          <table className="w-full min-w-[700px]" data-id="element-525">
            <thead className="bg-background-elevated/50" data-id="element-526">
              <tr className="border-b border-border-subtle" data-id="element-527">
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-left" data-id="element-528">
                  Date
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-left" data-id="element-529">
                  Side
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-left" data-id="element-530">
                  Type
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-left" data-id="element-531">
                  Pair
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-532">
                  Price
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-533">
                  Volume
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-534">
                  Cost
                </th>
                <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-535">
                  ID
                </th>
              </tr>
            </thead>
            <tbody data-id="element-536">
              {filteredData.map(trade => <tr key={trade.trade_id} className="border-b border-border-subtle hover:bg-background-elevated/30 transition-colors" data-id="element-537">
                  <td className="px-6 py-4 text-sm text-text-primary" data-id="element-538">
                    {trade.date}
                  </td>
                  <td className="px-6 py-4 text-sm text-text-primary" data-id="element-539">
                    {trade.side}
                  </td>
                  <td className="px-6 py-4 text-sm text-text-primary" data-id="element-540">
                    {trade.type}
                  </td>
                  <td className="px-6 py-4" data-id="element-541">
                    <div className="flex items-center gap-2" data-id="element-542">
                      <img src={getIconUrl(trade.pair)} alt={trade.pair} className="w-5 h-5 object-contain" data-id="element-543" />
                      <span className="text-sm font-medium text-text-primary" data-id="element-544">
                        {trade.pair}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-545">
                    <div className="font-numbers text-sm text-text-primary" data-id="element-546">
                      {trade.price.toLocaleString('en-US', {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 4
                  })}
                      <span className="text-text-secondary text-xs ml-1" data-id="element-547">
                        USD
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-548">
                    <div className="font-numbers text-sm text-text-primary" data-id="element-549">
                      {trade.volume.toFixed(8)}
                      <span className="text-text-secondary text-xs ml-1" data-id="element-550">
                        BTC
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-551">
                    <div className="font-numbers text-sm text-text-primary" data-id="element-552">
                      {trade.cost.toFixed(2)}
                      <span className="text-text-secondary text-xs ml-1" data-id="element-553">
                        USD
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-554">
                    <div className="font-numbers text-xs text-text-secondary" data-id="element-555">
                      {trade.trade_id}
                    </div>
                  </td>
                </tr>)}
              {filteredData.length === 0 && <tr data-id="element-556">
                  <td colSpan={8} className="px-6 py-8 text-center text-text-secondary" data-id="element-557">
                    No trades found matching your filters.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-border-subtle flex items-center justify-between" data-id="element-558">
          <div className="text-sm text-text-secondary" data-id="element-559">
            {totalItems > 0 ? `1 - ${Math.min(itemsPerPage, totalItems)} of ${totalItems}` : '0 of 0'}
          </div>
          <div className="flex items-center gap-2" data-id="element-560">
            <button disabled={currentPage === 1} className="p-2 text-text-secondary hover:text-text-primary hover:bg-background-elevated rounded-lg transition-all disabled:opacity-50 disabled:hover:bg-transparent" data-id="element-561">
              <ChevronLeft size={16} data-id="element-562" />
            </button>
            <div className="text-sm font-medium text-text-primary px-2" data-id="element-563">
              Page {currentPage} of {totalPages}
            </div>
            <button disabled={currentPage === totalPages} className="p-2 text-text-secondary hover:text-text-primary hover:bg-background-elevated rounded-lg transition-all disabled:opacity-50 disabled:hover:bg-transparent" data-id="element-564">
              <ChevronRight size={16} data-id="element-565" />
            </button>
          </div>
        </div>
      </div>
    </div>;
}